import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './AgencySection.module.css';
import { ShowReel } from '../ShowReel';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTextReveal } from '@/hooks/useRevealAnimations';
import { useGSAP } from '@/hooks/useGSAP';
import { useLenis } from '@/contexts/LenisContext';

interface AgencySectionProps {
  className?: string;
}

export const AgencySection: React.FC<AgencySectionProps> = ({ className }) => {
  const { t } = useLanguage();
  const { lenis } = useLenis();
  const { gsap, ready } = useGSAP();
  // Use state + callback ref to guarantee re-render after mount
  const [textContainer, setTextContainer] = useState<HTMLDivElement | null>(
    null
  );
  const textContainerRef = useCallback((node: HTMLDivElement | null) => {
    setTextContainer(node);
  }, []);
  const instructionsContainerRef = useRef<HTMLDivElement | null>(null);

  useTextReveal(textContainer, {
    stagger: 0.3,
    duration: 1,
  });

  useEffect(() => {
    if (!ready || !gsap) return;
    if (!lenis) return;

    const instructions = instructionsContainerRef.current;
    if (!instructions) return;

    gsap.fromTo(
      instructions,
      { autoAlpha: 0 },
      { autoAlpha: 1, delay: 0.6, duration: 0.5, ease: 'power3.out' }
    );

    let lastState = true; // Track state to avoid redundant animations
    const scrollHandler = (e: { scroll: number }) => {
      const shouldBeVisible = e.scroll <= 50;
      if (shouldBeVisible !== lastState) {
        lastState = shouldBeVisible;
        gsap.to(instructions, {
          autoAlpha: shouldBeVisible ? 1 : 0,
          duration: 0.5,
          ease: 'power3.out',
        });
      }
    };

    lenis.on('scroll', scrollHandler);

    return () => {
      lenis.off('scroll', scrollHandler);
    };
  }, [lenis, ready, gsap]);

  return (
    <section
      className={`${styles.agencySection} ${className || ''}`}
      id="agency-section"
      aria-label="Agency Section"
    >
      <div className={styles.container}>
        <div className={styles.heroTextFrame}>
          <div
            className={`textReveal ${styles.textRevealContainer}`}
            ref={textContainerRef}
          >
            <h1 className={styles.inventiveText}>
              <span
                className={styles.gradientText}
                data-text={t('home-hero.title')}
              >
                {t('home.hero.title')}
              </span>
            </h1>
            <h2 className={styles.agencyText}>
              <span>{t('home.hero.subtitle')}</span>
            </h2>
          </div>
        </div>
        <div
          ref={instructionsContainerRef}
          className={`
            ${styles.instructionsContainer}
            textReveal
          `}
        >
          <span>(Scroll)</span>
        </div>
      </div>
      <ShowReel className={styles.showReelInner} />
    </section>
  );
};
